/**
 * Participation hand-off (Planning Resource Model PRD, Part D).
 *
 * Every write here goes through a transactional SECURITY DEFINER RPC
 * (Build Contract §2.3a, migration 690), because the whole point of this slice
 * is that a status change is EVIDENCE — a claim by the counterpart — rather
 * than a checkbox the requester ticks on their behalf.
 */

import { getSupabaseClient } from './supabaseClient';
import { getTeamParticipation } from './planningEngineService';
import type {
  ParticipationPeriodCommitment,
  TeamParticipation,
} from './planningEngineModule.types';

export type ParticipationResponse = 'confirmed' | 'tentative' | 'declined';

/** A request has been handed to its counterpart and is awaiting an answer. */
export function isAwaitingResponse(row: TeamParticipation): boolean {
  return row.requestedAt != null && row.respondedAt == null;
}

/** A request the counterpart has answered. */
export function isAnswered(row: TeamParticipation): boolean {
  return row.requestedAt != null && row.respondedAt != null;
}

/** Past its deadline with no answer — the thing an inbox exists to surface. */
export function isOverdue(row: TeamParticipation, today = new Date()): boolean {
  if (!isAwaitingResponse(row) || !row.participationDeadline) return false;
  return Date.parse(row.participationDeadline) < today.getTime();
}

/**
 * Requests addressed to one person.
 *
 * Filtered client-side over the workspace's participation rows rather than by a
 * scoped query, because every consumer of this inbox already holds them: the
 * risk overlay, the priority views and the time-phased workbench all read the
 * same list.
 */
export function inboxFor(rows: TeamParticipation[], userId: string): TeamParticipation[] {
  return rows
    .filter((r) => r.participationOwnerId === userId && r.requestedAt != null)
    .sort((a, b) => {
      // Unanswered first, then by deadline, then by when it was asked.
      const aOpen = isAwaitingResponse(a) ? 0 : 1;
      const bOpen = isAwaitingResponse(b) ? 0 : 1;
      if (aOpen !== bOpen) return aOpen - bOpen;
      const aDue = a.participationDeadline ?? '9999';
      const bDue = b.participationDeadline ?? '9999';
      if (aDue !== bDue) return aDue.localeCompare(bDue);
      return (a.requestedAt ?? '').localeCompare(b.requestedAt ?? '');
    });
}

export async function getParticipationInbox(
  projectId: string,
  userId: string,
): Promise<TeamParticipation[]> {
  return inboxFor(await getTeamParticipation(projectId), userId);
}

export async function sendParticipationRequest(params: {
  participationId: string;
  ownerId?: string | null;
  deadline?: string | null;
}): Promise<void> {
  const { error } = await getSupabaseClient().rpc('send_participation_request', {
    p_participation_id: params.participationId,
    p_owner_id: params.ownerId ?? null,
    p_deadline: params.deadline ?? null,
  });
  if (error) throw error;
}

export async function respondToParticipationRequest(params: {
  participationId: string;
  response: ParticipationResponse;
  note?: string | null;
  periodCommitments?: ParticipationPeriodCommitment[] | null;
}): Promise<void> {
  const { error } = await getSupabaseClient().rpc('respond_to_participation_request', {
    p_participation_id: params.participationId,
    p_response: params.response,
    p_note: params.note ?? null,
    p_period_commitments: params.periodCommitments
      ? params.periodCommitments.map((c) => ({
          bucketStart: c.bucketStart,
          bucketEnd: c.bucketEnd ?? null,
          hours: c.hours ?? null,
          status: c.status,
        }))
      : null,
  });
  if (error) throw error;
}

/**
 * May this person withdraw a sent participation request? Mirrors the check in
 * withdraw_participation_request (migration 690): the person who sent it, or a
 * workspace admin or owner (`isAdmin` — the caller passes can('admin.settings'),
 * which every admin role holds). The function stays the authority.
 */
export function participationWithdrawAllowed({
  userId,
  requestedBy,
  isAdmin,
}: {
  userId: string | null | undefined;
  requestedBy: string | null | undefined;
  isAdmin: boolean;
}): boolean {
  if (!userId) return false;
  if (requestedBy && requestedBy === userId) return true;
  return isAdmin;
}

export async function withdrawParticipationRequest(participationId: string): Promise<void> {
  const { error } = await getSupabaseClient().rpc('withdraw_participation_request', {
    p_participation_id: participationId,
  });
  if (error) throw error;
}
